import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Check } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { cn, getImageUrl } from '@/lib/utils';
import type { ProductAssociations, VariationType, ProductGroupItem, AssociatedProduct } from '@/lib/types';
import { ColorSwatches } from './ColorSwatches';
import { SizeChips } from './SizeChips';
import { AttributeChips } from './AttributeChips';

interface AssociatedProductsDisplayProps {
  associations: ProductAssociations;
  currentProductId: string;
  onProductClick?: (refId: string) => void;
}

// Get title translation key based on variation type
function getTitleKey(variationType?: VariationType): string {
  switch (variationType) {
    case 'color':
      return 'variations.color';
    case 'size':
      return 'variations.size';
    case 'size_color':
      return 'variations.sizeColor';
    case 'style':
      return 'variations.style';
    case 'pattern':
      return 'variations.pattern';
    case 'material':
      return 'variations.material';
    case 'flavor':
      return 'variations.flavor';
    case 'quantity':
      return 'variations.quantity';
    case 'related':
    default:
      return 'variations.related';
  }
}

// Component for rendering size + color combinations
function SizeColorVariants({
  items,
  currentProductId,
  onProductClick,
}: {
  items: ProductGroupItem[];
  currentProductId: string;
  onProductClick?: (refId: string) => void;
}) {
  // Group items by size
  const groupedBySize = items.reduce((acc, item) => {
    const size = item.sizeName || 'default';
    if (!acc[size]) {
      acc[size] = [];
    }
    acc[size].push(item);
    return acc;
  }, {} as Record<string, ProductGroupItem[]>);

  return (
    <div className="space-y-3">
      {Object.entries(groupedBySize).map(([size, colorItems]) => (
        <div key={size}>
          <p className="text-sm text-muted-foreground mb-2 font-medium">{size}</p>
          <ColorSwatches
            items={colorItems}
            currentProductId={currentProductId}
            onProductClick={onProductClick}
          />
        </div>
      ))}
    </div>
  );
}

// Component for rendering related products as cards
function RelatedProductsGrid({
  items,
  products,
  currentProductId,
  onProductClick,
}: {
  items?: ProductGroupItem[];
  products?: AssociatedProduct[];
  currentProductId: string;
  onProductClick?: (refId: string) => void;
}) {
  // Use items (new format) if available, otherwise use products (legacy format)
  const itemsToRender = items && items.length > 0
    ? items.map((item) => ({
        id: item.product.refId,
        name: item.product.name,
        image: item.product.image ? getImageUrl(item.product.image) : undefined,
      }))
    : products?.map((product) => ({
        id: product.id,
        name: product.name,
        image: product.image,
      })) || [];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {itemsToRender.map((item) => {
        const isCurrent = item.id === currentProductId;

        return (
          <Link
            key={item.id}
            to={onProductClick ? '#' : `/products/${item.id}`}
            onClick={(e) => {
              if (onProductClick) {
                e.preventDefault();
                if (!isCurrent) onProductClick(item.id);
              }
            }}
            className={cn(
              'group block',
              isCurrent && 'pointer-events-none'
            )}
          >
            <div className="space-y-2">
              <div
                className={cn(
                  'relative aspect-square rounded-lg overflow-hidden bg-muted border transition-colors',
                  isCurrent
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'group-hover:border-primary'
                )}
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <Package className="size-8" />
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute top-1 right-1 size-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="size-3 text-primary-foreground" />
                  </div>
                )}
              </div>
              <p
                className={cn(
                  'text-xs truncate text-center',
                  isCurrent ? 'text-primary font-medium' : 'group-hover:text-primary'
                )}
              >
                {item.name}
              </p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function AssociatedProductsDisplay({
  associations,
  currentProductId,
  onProductClick,
}: AssociatedProductsDisplayProps) {
  const { t } = useTranslation();

  const items = associations.items || [];
  const products = associations.products || [];
  const variationType = associations.variationType;

  // Count total items (including current product)
  const totalCount = items.length > 0 ? items.length : products.length;

  if (totalCount === 0) {
    return null;
  }

  const titleKey = getTitleKey(variationType);

  // Render the appropriate variant component based on type
  const renderVariants = () => {
    switch (variationType) {
      case 'color':
        return (
          <ColorSwatches
            items={items}
            currentProductId={currentProductId}
            onProductClick={onProductClick}
          />
        );

      case 'size':
        return (
          <SizeChips
            items={items}
            currentProductId={currentProductId}
            onProductClick={onProductClick}
          />
        );

      case 'size_color':
        return (
          <SizeColorVariants
            items={items}
            currentProductId={currentProductId}
            onProductClick={onProductClick}
          />
        );

      case 'style':
      case 'pattern':
      case 'material':
      case 'flavor':
      case 'quantity':
        return (
          <AttributeChips
            items={items}
            currentProductId={currentProductId}
            variationType={variationType}
            onProductClick={onProductClick}
          />
        );

      case 'related':
      default:
        return (
          <RelatedProductsGrid
            items={items}
            products={products}
            currentProductId={currentProductId}
            onProductClick={onProductClick}
          />
        );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="font-medium text-sm">{t(titleKey)}</h4>
        <Badge variant="secondary" className="text-xs">
          {totalCount} {t('variations.available')}
        </Badge>
      </div>
      {renderVariants()}
    </div>
  );
}
